// DaisyUI styling; remove legacy CSS import
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  Bars3BottomLeftIcon,
  PlusIcon,
  SpeakerWaveIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/solid';
import { useState, useEffect } from 'react';
import {
  CREATE_TAGS,
  DELETE_TAGS,
  EDIT_TAGS,
  LIST_TAGS,
} from '~/services/teams';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import TagDialog from './TagDialog';
import {
  createTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  useReactTable,
} from '@tanstack/react-table';
import { set } from 'lodash';
import { ACTIONS } from '~/types';

// Render DB color directly to avoid Tailwind mapping limitations

interface RolPermissionsProps {
  searchValue: string;
  selectedFilter: string;
  selectedSort: string;
  isAddClicked: boolean;
  setIsAddClicked: (value: boolean) => void;
}

export default function Tags({
  searchValue,
  selectedFilter,
  selectedSort,
  isAddClicked,
  setIsAddClicked,
}: RolPermissionsProps) {
  const [t] = useTranslation('teams');
  const [isEditModal, setIsEditModal] = useState(false);

  const [createTags] = useMutation(CREATE_TAGS);
  const [deleteTags] = useMutation(DELETE_TAGS);
  const [editTags] = useMutation(EDIT_TAGS);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [notificationSound, setNotificationSound] = useState(false);
  const [tagAction, setTagAction] = useState(ACTIONS.KEEP_CONVERSATION);
  const [tagColor, setTagColor] = useState('#E0E0E0'); // Default color
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(
    null,
  );

  const { agentId } = useParams();
  const { data, loading } = useQuery(LIST_TAGS, {
    variables: { data: { agent_id: agentId } },
  });

  const [tags, setTags] = useState<
    {
      name: string;
      description: string;
      _id: string;
      notification_sound: boolean;
      action: string;
      color: string;
    }[]
  >([]);
  const [filteredTags, setFilteredTags] = useState(tags);

  useEffect(() => {
    if (data && data.list_tags && data.list_tags.data) {
      setTags(data.list_tags.data);
    }
  }, [data]);

  useEffect(() => {
    try {
      const term = searchValue.trim().toLowerCase();
      const filterField = (selectedFilter || 'name') as keyof (typeof tags)[number];
      const sortField = (selectedSort || 'name') as keyof (typeof tags)[number];

      let list = [...tags];

      if (term) {
        list = list.filter((tag) =>
          String(tag[filterField] ?? '').toLowerCase().includes(term),
        );
      }

      list = list.sort((a, b) =>
        String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? '')),
      );

      setFilteredTags(list);
    } catch (error) {
      setAlert({ type: 'error', message: t('internal_error') });
    }
  }, [tags, searchValue, selectedFilter, selectedSort, t]);

  const closeDialog = () => {
    setIsAddClicked(false);
    setIsEditModal(false);
    setOpenDropdownId(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const res = await createTags({
        variables: {
          data: {
            agent_id: agentId,
            name: tagName,
            description: tagDescription,
            notification_sound: notificationSound,
            action: tagAction,
            color: tagColor,
          },
        },
      });
      console.log(res.data);
      setTags([...tags, res.data.create_tags.data]);
      setTagName('');
      setTagDescription('');
      setNotificationSound(false);
      setTagAction(ACTIONS.KEEP_CONVERSATION);
      setTagColor('#E0E0E0');
      setAlert({ type: 'success', message: t('save_success') });
    } catch (error) {
      setAlert({ type: 'error', message: t('save_error') });
    }
    setTimeout(() => setAlert(null), 3000);
    closeDialog();
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const res = await editTags({
        variables: {
          data: {
            tag_id: openDropdownId,
            name: tagName,
            description: tagDescription,
            notification_sound: notificationSound,
            action: tagAction,
            color: tagColor,
          },
        },
      });
      setTags(
        tags.map((tag) =>
          tag._id === openDropdownId ? res.data.update_tags.data : tag,
        ),
      );
      setAlert({ type: 'success', message: t('edit_success') });
    } catch (error) {
      setAlert({ type: 'error', message: t('edit_error') });
    }
    setTimeout(() => setAlert(null), 3000);
    setIsEditModal(false);
    setTagName('');
    setTagDescription('');
    setNotificationSound(false);
    setTagAction(ACTIONS.KEEP_CONVERSATION);
    setTagColor('#E0E0E0');
    setOpenDropdownId(null);
    closeDialog();
  };

  const editTag = (
    _id: string,
    name: string,
    description: string,
    notification_sound: boolean,
    action: ACTIONS,
    color: string,
  ) => {
    setTagName(name);
    setTagDescription(description);
    setNotificationSound(notification_sound);
    setTagAction(action);
    setTagColor(color);
    setIsEditModal(true);
  };

  const deleteTag = async (tagId: string) => {
    try {
      const res = await deleteTags({
        variables: {
          data: {
            tag_id: tagId,
          },
        },
      });
      setTags(tags.filter((tag) => tag._id !== tagId));
    } catch (error) {
      setAlert({ type: 'error', message: t('delete_error') });
    }
    setTimeout(() => setAlert(null), 3000);
  };

  useEffect(() => {
    if (isAddClicked) {
      setTagName('');
      setTagDescription('');
      setNotificationSound(false);
      setTagAction(ACTIONS.KEEP_CONVERSATION);
      setTagColor('#E0E0E0');
      setIsEditModal(false);
    }
  }, [isAddClicked]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.actions')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const columns: ColumnDef<{
    name: string;
    description: string;
    _id: string;
    notification_sound: boolean;
    action: string;
    color: string;
  }>[] = [
    {
      header: t('name'),
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full border border-base-200"
            style={{ backgroundColor: row.original.color || '#E0E0E0' }}
          />
          {row.original.name}
        </div>
      ),
    },
    {
      header: t('description'),
      accessorKey: 'description',
    },
    {
      header: t('notification'),
      accessorKey: 'notification_sound',
      cell: ({ row }) => (
        <div className="flex h-full min-h-[40px] w-full items-center justify-start gap-2">
          {row.original.action === ACTIONS.REDIRECT_AND_NOTIFY && (
            <ChatBubbleLeftIcon
              className="h-5 w-5  "
              title={t('redirect_and_notify_explication')}
              aria-label={t('redirect_and_notify')}
            />
          )}
          {row.original.notification_sound && (
            <SpeakerWaveIcon
              className="h-5 w-5  "
              title={t('notification_sound')}
              aria-label={t('notification_sound')}
            />
          )}
        </div>
      ),
    },
    {
      header: t('actions'),
      cell: ({ row }) => (
        <div className="relative">
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => setOpenDropdownId(row.original._id)}
          >
            ⋮
          </button>
          {openDropdownId === row.original._id && (
            <div className="dropdown-content menu absolute left-0 z-10 mt-1 w-44 rounded-box bg-base-100 p-2 shadow">
              <li>
                <button
                  onClick={() => {
                    editTag(
                      row.original._id,
                      row.original.name,
                      row.original.description,
                      row.original.notification_sound,
                      row.original.action as ACTIONS,
                      row.original.color,
                    );
                  }}
                >
                  {t('edit')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    deleteTag(row.original._id);
                  }}
                >
                  {t('delete')}
                </button>
              </li>
            </div>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredTags,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full">
      {alert && (
        <div
          className={`alert ${alert.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}
          role="alert"
        >
          {alert.message}
        </div>
      )}
      {loading && <div>{t('loading')}</div>}
      <div className="overflow-x-auto rounded-box bg-base-100 shadow">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TagDialog
        isOpen={isAddClicked || isEditModal}
        isEditModal={isEditModal}
        tagName={tagName}
        tagDescription={tagDescription}
        notificationSound={notificationSound}
        tagAction={tagAction}
        tagColor={tagColor}
        handleSubmit={isEditModal ? handleEditSubmit : handleSubmit}
        setTagName={setTagName}
        setTagDescription={setTagDescription}
        setNotificationSound={setNotificationSound}
        setTagAction={setTagAction}
        setTagColor={setTagColor}
        closeDialog={closeDialog}
      />
    </div>
  );
}
